import { useState, useEffect, useCallback } from 'react';
import { waitlistApi } from '@verlo/shared/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@verlo/shared/components/ui/card';
import { Button } from '@verlo/shared/components/ui/button';
import { Badge } from '@verlo/shared/components/ui/badge';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@verlo/shared/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@verlo/shared/components/ui/select';
import { Textarea } from '@verlo/shared/components/ui/textarea';
import { toast } from 'sonner';
import { 
  ListOrdered, Plus, Users, Clock, Bell, ArrowRight, X, Timer,
} from 'lucide-react';

const WaitlistPanel = ({ tables, onSeatGuest, onRefresh }) => {
  const [entries, setEntries] = useState([]);
  const [waitCount, setWaitCount] = useState({ parties_waiting: 0, total_guests: 0 });
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    party_size: 2,
    notes: '',
    quoted_wait_minutes: 30,
  });

  const fetchWaitlist = useCallback(async () => {
    try {
      const [entriesRes, countRes] = await Promise.all([
        waitlistApi.getAll('waiting'),
        waitlistApi.getCount(),
      ]);
      setEntries(entriesRes.data || []);
      setWaitCount(countRes.data || { parties_waiting: 0, total_guests: 0 });
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    fetchWaitlist();
    const interval = setInterval(fetchWaitlist, 30000);
    return () => clearInterval(interval);
  }, [fetchWaitlist]);

  const handleAdd = async () => {
    if (!formData.customer_name.trim()) {
      toast.error('Name is required');
      return;
    }
    try {
      await waitlistApi.add(formData);
      toast.success(`${formData.customer_name} added to waitlist`);
      setAddDialogOpen(false);
      setFormData({ customer_name: '', customer_phone: '', party_size: 2, notes: '', quoted_wait_minutes: 30 });
      fetchWaitlist();
    } catch {
      toast.error('Failed to add to waitlist');
    }
  };

  const handleNotify = async (entry) => {
    try {
      await waitlistApi.notify(entry.id);
      toast.success(`${entry.customer_name} notified`);
      fetchWaitlist();
    } catch {
      toast.error('Failed to notify guest');
    }
  };

  const handleSeat = async (entry) => {
    try {
      await waitlistApi.seat(entry.id);
      toast.success(`${entry.customer_name} seated`);
      fetchWaitlist();
      onRefresh?.();
    } catch {
      toast.error('Failed to seat guest');
    }
  };

  const handleRemove = async (entry) => {
    try {
      await waitlistApi.remove(entry.id);
      toast.success(`${entry.customer_name} removed`);
      fetchWaitlist();
    } catch {
      toast.error('Failed to remove');
    }
  };

  const getWaitTime = (addedAt) => {
    const added = new Date(addedAt);
    const now = new Date();
    const mins = Math.floor((now - added) / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m`;
    return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  };

  return (
    <>
      <Card className="border shadow-sm" data-testid="waitlist-panel">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <ListOrdered size={16} />
              Waitlist
              {waitCount.parties_waiting > 0 && (
                <Badge className="bg-signal-warn-line text-white ml-1">
                  {waitCount.parties_waiting}
                </Badge>
              )}
            </CardTitle>
            <Button 
              size="sm" 
              className="h-7 text-xs bg-verlo hover:bg-verlo-hover"
              onClick={() => setAddDialogOpen(true)}
              data-testid="add-to-waitlist-btn"
            >
              <Plus size={12} className="mr-1" />
              Add
            </Button>
          </div>
          {waitCount.parties_waiting > 0 && (
            <p className="text-xs text-ink-soft mt-1">
              {waitCount.parties_waiting} {waitCount.parties_waiting === 1 ? 'party' : 'parties'} · {waitCount.total_guests} guests waiting
            </p>
          )}
        </CardHeader>
        <CardContent>
          {entries.length === 0 ? (
            <div className="text-center py-6 text-ink-faint text-sm">
              <ListOrdered size={24} className="mx-auto mb-2 opacity-40" />
              No one waiting
            </div>
          ) : (
            <div className="space-y-2 max-h-[280px] overflow-y-auto">
              {entries.map((entry) => (
                <div 
                  key={entry.id}
                  className={`p-3 border transition-all ${
                    entry.status === 'notified'
                      ? 'bg-signal-warn-tint border-signal-warn-line'
                      : 'bg-white border-hairline'
                  }`}
                  data-testid={`waitlist-entry-${entry.id}`}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm text-ink">
                        #{entry.position} {entry.customer_name}
                      </span>
                      {entry.status === 'notified' && (
                        <Badge className="bg-signal-warn-tint text-signal-warn text-[10px]">Notified</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-ink-soft">
                      <Timer size={10} />
                      {getWaitTime(entry.added_at)}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-ink-soft flex items-center gap-3">
                      <span className="flex items-center gap-1">
                        <Users size={11} /> {entry.party_size}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={11} /> ~{entry.quoted_wait_minutes}m quoted
                      </span>
                    </div>
                    <div className="flex gap-1">
                      {entry.status !== 'notified' && (
                        <Button 
                          size="sm" variant="outline" 
                          className="h-6 w-6 p-0"
                          onClick={() => handleNotify(entry)}
                          title="Notify guest"
                          data-testid={`notify-btn-${entry.id}`}
                        >
                          <Bell size={12} />
                        </Button>
                      )}
                      <Button 
                        size="sm" variant="outline" 
                        className="h-6 w-6 p-0 text-signal-ok hover:bg-signal-ok-tint"
                        onClick={() => handleSeat(entry)}
                        title="Seat guest"
                        data-testid={`seat-waitlist-btn-${entry.id}`}
                      >
                        <ArrowRight size={12} />
                      </Button>
                      <Button 
                        size="sm" variant="outline" 
                        className="h-6 w-6 p-0 text-signal-stop hover:bg-signal-stop-tint"
                        onClick={() => handleRemove(entry)}
                        title="Remove"
                        data-testid={`remove-waitlist-btn-${entry.id}`}
                      >
                        <X size={12} />
                      </Button>
                    </div>
                  </div>
                  {entry.notes && (
                    <p className="text-[11px] text-ink-soft mt-1 truncate">{entry.notes}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add to Waitlist Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent size="confirm">
          <DialogHeader>
            <DialogTitle>Add to Waitlist</DialogTitle>
            <DialogDescription>Add a walk-in party to the queue</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                placeholder="Guest name"
                value={formData.customer_name}
                onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                data-testid="waitlist-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                placeholder="+44 7700 900000"
                value={formData.customer_phone}
                onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                data-testid="waitlist-phone-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Party Size</Label>
                <Select
                  value={formData.party_size.toString()}
                  onValueChange={(v) => setFormData({ ...formData, party_size: parseInt(v) })}
                >
                  <SelectTrigger data-testid="waitlist-party-size">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1,2,3,4,5,6,7,8,10,12].map(n => (
                      <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Est. Wait</Label>
                <Select
                  value={formData.quoted_wait_minutes.toString()}
                  onValueChange={(v) => setFormData({ ...formData, quoted_wait_minutes: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">15 min</SelectItem>
                    <SelectItem value="30">30 min</SelectItem>
                    <SelectItem value="45">45 min</SelectItem>
                    <SelectItem value="60">1 hour</SelectItem>
                    <SelectItem value="90">1.5 hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Any notes..."
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="min-h-[50px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAdd} className="bg-verlo hover:bg-verlo-hover" data-testid="confirm-add-waitlist">
              Add to Waitlist
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default WaitlistPanel;
